import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { ArrowLeft, Palette, Lock, Sparkles, Star, Shuffle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { auth } from '@/config/firebase'
import { getUserSubscription, isPremiumSubscription } from '@/services/subscriptionService'

export default function CharacterCreatorInfoPage() {
  const [isPremium, setIsPremium] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const checkAccess = async () => {
      const user = auth.currentUser
      if (user) {
        const subscription = await getUserSubscription(user.uid)
        setIsPremium(isPremiumSubscription(subscription))
      }
      setLoading(false)
    }
    checkAccess()
  }, [])

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Back Button */}
      <Link to="/" className="inline-flex items-center text-dark-navy/70 hover:text-dark-navy mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Games
      </Link>

      {/* Hero Section */}
      <div className="text-center mb-12">
        <div className="inline-block relative">
          <div className="text-6xl mb-4">🎨</div>
          {!loading && !isPremium && (
            <div className="absolute -top-2 -right-8 bg-medium-purple text-white text-xs px-2 py-1 rounded-full font-bold flex items-center gap-1">
              <Lock className="w-3 h-3" />
              Premium
            </div>
          )}
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-dark-navy mb-4">Character Creator</h1>
        <p className="text-xl text-dark-navy/70 max-w-2xl mx-auto">
          Design your very own unique character! Choose from fun styles, customise every detail, 
          and use your creation in Ocean Dash, Pollution Patrol, and more!
        </p>
        
        {/* CTA Button */}
        <div className="mt-8">
          {loading ? (
            <div className="inline-flex items-center justify-center px-8 py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-medium-purple border-t-transparent"></div>
            </div>
          ) : isPremium ? (
            <Link to="/character-creation">
              <Button size="lg" className="text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all bg-medium-teal hover:bg-light-teal text-white">
                <Palette className="w-5 h-5 mr-2" />
                Start Creating
              </Button>
            </Link>
          ) : (
            <Link to="/subscription">
              <Button size="lg" className="text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all bg-medium-purple hover:bg-deep-purple text-white">
                <Lock className="w-5 h-5 mr-2" />
                Unlock Premium
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Screenshot Section */}
      <div className="mb-12">
        <div className="bg-gradient-to-br from-medium-purple/20 to-medium-teal/20 rounded-2xl overflow-hidden shadow-xl">
          <img 
            src="/screenshots/character_creator.jpg" 
            alt="Character Creator screenshot"
            className="w-full h-auto object-cover"
          />
        </div>
      </div>

      {/* Character Styles */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-white/40">
        <h2 className="text-2xl font-bold text-dark-navy mb-6 flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-medium-purple" />
          Choose Your Style
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="p-4 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl border-2 border-green-200 text-center">
            <span className="text-3xl">🧙</span>
            <h3 className="font-bold text-green-700 mt-2">Explorer</h3>
            <p className="text-xs text-dark-navy/60 mt-1">Adventure-ready characters</p>
          </div>
          
          <div className="p-4 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl border-2 border-blue-200 text-center">
            <span className="text-3xl">🤖</span>
            <h3 className="font-bold text-blue-700 mt-2">Robot</h3>
            <p className="text-xs text-dark-navy/60 mt-1">Cool mechanical friends</p>
          </div>
          
          <div className="p-4 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-xl border-2 border-yellow-200 text-center">
            <span className="text-3xl">😄</span>
            <h3 className="font-bold text-yellow-700 mt-2">Emoji</h3>
            <p className="text-xs text-dark-navy/60 mt-1">Fun expressive faces</p>
          </div>
          
          <div className="p-4 bg-gradient-to-br from-pink-100 to-rose-100 rounded-xl border-2 border-pink-200 text-center">
            <span className="text-3xl">✨</span>
            <h3 className="font-bold text-pink-700 mt-2">Portrait</h3>
            <p className="text-xs text-dark-navy/60 mt-1">Elegant character art</p>
          </div>
          
          <div className="p-4 bg-gradient-to-br from-purple-100 to-violet-100 rounded-xl border-2 border-purple-200 text-center">
            <span className="text-3xl">👾</span>
            <h3 className="font-bold text-purple-700 mt-2">Pixel</h3>
            <p className="text-xs text-dark-navy/60 mt-1">Retro pixel art style</p>
          </div>
          
          <div className="p-4 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-xl border-2 border-teal-200 text-center">
            <span className="text-3xl">👍</span>
            <h3 className="font-bold text-teal-700 mt-2">Thumbs</h3>
            <p className="text-xs text-dark-navy/60 mt-1">Quirky thumb characters</p>
          </div>
        </div>
      </div>

      {/* Customisation Options */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-white/40">
        <h2 className="text-2xl font-bold text-dark-navy mb-6 flex items-center gap-2">
          <Palette className="w-6 h-6 text-bright-coral" />
          Customisation Options
        </h2>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3 p-4 bg-white/50 rounded-xl">
            <span className="text-2xl">🎨</span>
            <div>
              <h3 className="font-semibold text-dark-navy">Background Colours</h3>
              <p className="text-dark-navy/70 text-sm">Choose from a rainbow of beautiful background options</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 bg-white/50 rounded-xl">
            <span className="text-2xl">👁️</span>
            <div>
              <h3 className="font-semibold text-dark-navy">Eyes</h3>
              <p className="text-dark-navy/70 text-sm">Pick the perfect eyes to express your character</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 bg-white/50 rounded-xl">
            <span className="text-2xl">👄</span>
            <div>
              <h3 className="font-semibold text-dark-navy">Mouth</h3>
              <p className="text-dark-navy/70 text-sm">Give your character a smile, grin, or fun expression</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 bg-white/50 rounded-xl">
            <span className="text-2xl">💇</span>
            <div>
              <h3 className="font-semibold text-dark-navy">Hair & Accessories</h3>
              <p className="text-dark-navy/70 text-sm">Style your character with different hair and extras</p>
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-white/40">
        <h2 className="text-2xl font-bold text-dark-navy mb-6 flex items-center gap-2">
          <Star className="w-6 h-6 text-medium-teal" />
          Features
        </h2>
        
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-medium-teal/10 to-transparent rounded-xl">
            <Shuffle className="w-6 h-6 text-medium-teal" />
            <div>
              <h3 className="font-semibold text-dark-navy">Randomise Button</h3>
              <p className="text-dark-navy/70 text-sm">Can't decide? Let the magic button create a random character for you!</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-bright-coral/10 to-transparent rounded-xl">
            <span className="text-2xl">💾</span>
            <div>
              <h3 className="font-semibold text-dark-navy">Save to Profile</h3>
              <p className="text-dark-navy/70 text-sm">Your character is saved to your child profile automatically</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-medium-purple/10 to-transparent rounded-xl">
            <span className="text-2xl">🎮</span>
            <div>
              <h3 className="font-semibold text-dark-navy">Use in Games</h3>
              <p className="text-dark-navy/70 text-sm">Your character appears in Ocean Dash and Pollution Patrol!</p>
            </div>
          </div>
        </div>
      </div>

      {/* Games That Use Your Character */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-white/40">
        <h2 className="text-2xl font-bold text-dark-navy mb-6">Your Character in Games</h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          <Link to="/ocean-dash" className="group">
            <div className="p-6 bg-gradient-to-br from-bright-coral/10 to-sandy-coral/20 rounded-xl border-2 border-bright-coral/30 hover:border-bright-coral transition-colors">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">🧜‍♀️</span>
                <h3 className="font-bold text-dark-navy group-hover:text-bright-coral transition-colors">Ocean Dash</h3>
              </div>
              <p className="text-dark-navy/70 text-sm">Swim through the ocean as your custom character!</p>
            </div>
          </Link>
          
          <Link to="/pollution-patrol" className="group">
            <div className="p-6 bg-gradient-to-br from-medium-teal/10 to-light-teal/20 rounded-xl border-2 border-medium-teal/30 hover:border-medium-teal transition-colors">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">♻️</span>
                <h3 className="font-bold text-dark-navy group-hover:text-medium-teal transition-colors">Pollution Patrol</h3>
              </div>
              <p className="text-dark-navy/70 text-sm">Jump and explore with your unique hero!</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="text-center py-8">
        {isPremium ? (
          <Link to="/character-creation">
            <Button size="lg" className="text-lg px-10 py-6 rounded-full shadow-lg hover:shadow-xl transition-all bg-medium-teal hover:bg-light-teal text-white">
              <Palette className="w-5 h-5 mr-2" />
              Create Your Character!
            </Button>
          </Link>
        ) : (
          <div className="space-y-4">
            <Link to="/subscription">
              <Button size="lg" className="text-lg px-10 py-6 rounded-full shadow-lg hover:shadow-xl transition-all bg-medium-purple hover:bg-deep-purple text-white">
                <Lock className="w-5 h-5 mr-2" />
                Unlock Premium to Create
              </Button>
            </Link>
            <p className="text-dark-navy/60 text-sm">
              Premium includes the Character Creator, all games, and supports ocean conservation!
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
